import React, { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { ReportSection } from '@/reports/index';
import { ContactCompany, ContactDetail } from '@/search/index';
import { Box, Button, Link } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { ContactResult } from './ContactResult';
import { ContactResultSkeleton } from './ContactResultSkeleton';

interface ContactListProps {
  /** The name of the contact report */
  personName: string;
  /** Whether the section has results */
  hasResults: boolean;
  /** Whether the contacts are loading */
  isLoading: boolean;
  /** The title of the section */
  title: string;
  /** The list of contacts */
  contacts: ContactDetail[];
  /** Whether to show a single company or multiple */
  showSingleCompany?: boolean;
  /** The handler for the click event in the company names associated with the contact */
  onCompanyClick: (company: string | ContactCompany) => void;
  /** The handler for the view report button */
  onViewReport: (contactId: string, subsection?: string) => void;
  /** The subsection name */
  subsection: string;
  header: JSX.Element;
}

/**
 * This component is responsible for displaying the list of contacts in a section.
 * It provides the ability to display a subset of contacts initially and allows the user to
 * expand or collapse the list.
 */
export function ContactList({
  personName,
  hasResults,
  isLoading,
  title,
  contacts,
  showSingleCompany = false,
  onCompanyClick,
  onViewReport,
  subsection,
  header,
}: ContactListProps) {
  const { isMobile } = useScreenSize();
  const { trackEvent } = useAppConfigContext();

  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    trackEvent(
      showSingleCompany
        ? 'related contacts_same company'
        : 'related contacts_same title',
      'expand/collapse',
      'contact report'
    );

    setExpanded(!expanded);
  };

  const maxDisplayedContacts = Math.min(10, contacts.length);
  const shouldShowButton = contacts.length >= 6;

  const displayedContacts = expanded
    ? contacts.slice(0, maxDisplayedContacts)
    : contacts.slice(0, 5);

  return (
    <ReportSection
      title={title}
      sectionId="key-contacts-section"
      hasContent={hasResults || contacts.length > 0}
      personName={personName}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        mt={2.5}
      >
        {!isMobile && header}

        {isLoading &&
          Array.from({
            length: 5,
          }).map((_, index) => <ContactResultSkeleton key={index} />)}

        {!isLoading &&
          displayedContacts.map((contact) => (
            <ContactResult
              key={contact.id}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...contact}
              showSingleCompany={showSingleCompany}
              onCompanyClick={onCompanyClick}
              onViewReport={onViewReport}
              subsection={subsection}
            />
          ))}
      </Box>
      {shouldShowButton && (
        <Button
          variant="outlined"
          color="primary"
          onClick={toggleExpand}
          sx={{ width: '180px', height: '42px', alignSelf: 'center' }}
        >
          {expanded ? 'Show less contacts' : 'Show more contacts'}
        </Button>
      )}
      <Link
        variant="body1"
        href="https://clearbit.com"
        target="_blank"
        rel="noopener"
        color="success.main"
        underline="hover"
      >
        Logos provided by Clearbit
      </Link>
    </ReportSection>
  );
}
